/**
 * F&O Trade Analysis — hero card. Top-of-panel summary: Signal chip,
 * Confidence %, Composite Score, Entry Zone / Stop Loss / Target 1 /
 * Target 2 / Risk-Reward / Expected Holding Period, plus a prominent
 * staleness warning (impossible to miss — always rendered inline, never a
 * dismissible/collapsed banner) whenever `is_stale` is true.
 *
 * Signal color convention matches this codebase's established green =
 * bullish / red = bearish / grey = neutral palette (see
 * `options-dashboard/colors.ts`'s `SEMANTIC_COLORS` and
 * `StrategyRecommendationSection.tsx`'s badge colors) — BUY green, SELL
 * red, HOLD grey.
 *
 * COMPLIANCE: this is decision-support only, never an instruction to trade
 * — the disclaimer line at the bottom is always rendered, never
 * conditionally hidden, matching `StrategyRecommendationSection.tsx`'s own
 * always-shown disclaimer convention.
 */
import { Alert, Box, Card, CardContent, Chip, Divider, Grid, LinearProgress, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { FnoTradeAnalysisResponse } from "@/types";
import { SEMANTIC_COLORS } from "@/pages/options-dashboard/colors";

export const FNO_ANALYSIS_DISCLAIMER =
  "Analytical decision-support only. This composite score, signal, and any entry/stop-loss/target " +
  "levels shown are mechanically calculated research outputs based on available market data, not a " +
  "prediction, guarantee, or personalized investment advice. Not a recommendation to buy or sell any " +
  "security. Users should conduct their own research or consult a SEBI-registered investment adviser " +
  "before making investment decisions.";

const SIGNAL_CHIP_COLOR: Record<string, "success" | "error" | "default"> = {
  BUY: "success",
  SELL: "error",
  HOLD: "default",
};

const SIGNAL_ACCENT: Record<string, string> = {
  BUY: SEMANTIC_COLORS.bullish,
  SELL: SEMANTIC_COLORS.bearish,
  HOLD: SEMANTIC_COLORS.neutral,
};

function fmtPrice(value?: number | null): string {
  if (value === null || value === undefined) return "—";
  return `₹${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtRatio(value?: number | null): string {
  if (value === null || value === undefined) return "—";
  return `${value.toFixed(2)}:1`;
}

function fmtDays(value?: number | null): string {
  if (value === null || value === undefined) return "—";
  return `${value} trading day${value === 1 ? "" : "s"}`;
}

function MetricTile({ label, value }: { label: string; value: string }) {
  return (
    <Grid item xs={6} sm={4} md={2}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={700}>
        {value}
      </Typography>
    </Grid>
  );
}

export function FnoHeroCard({ data }: { data: FnoTradeAnalysisResponse }) {
  const accent = SIGNAL_ACCENT[data.signal] ?? SEMANTIC_COLORS.neutral;
  const chipColor = SIGNAL_CHIP_COLOR[data.signal] ?? "default";
  const hasEntry =
    data.entry_low !== null && data.entry_low !== undefined && data.entry_high !== null && data.entry_high !== undefined;
  // The backend computes a single reference price (last close), so show it
  // as such rather than as a zero-width "zone".
  const entryZone = !hasEntry
    ? "—"
    : data.entry_low === data.entry_high
      ? `${fmtPrice(data.entry_low)} (last close)`
      : `${fmtPrice(data.entry_low)} – ${fmtPrice(data.entry_high)}`;

  return (
    <Card variant="outlined" sx={{ borderTop: `3px solid ${accent}` }}>
      <CardContent>
        {data.is_stale && (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2, fontWeight: 600 }}>
            Data may be stale: {data.staleness_reason ?? "the underlying market data used for this analysis is not current."}
          </Alert>
        )}

        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Chip
              label={data.signal}
              color={chipColor}
              sx={{ fontWeight: 800, fontSize: "1rem", px: 1.5, py: 2.5, height: "auto" }}
            />
            <Typography variant="h5" fontWeight={800}>
              {data.symbol}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            As of {new Date(data.captured_at).toLocaleString()}
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Composite Score
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              {data.composite_score.toFixed(1)} / 100
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Bullishness scale: 0 = strongly bearish, 50 = neutral, 100 = strongly bullish
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, data.composite_score))}
              sx={{ mt: 1, height: 8, borderRadius: 4 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Confidence
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              {data.confidence_pct.toFixed(0)}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, data.confidence_pct))}
              sx={{ mt: 1, height: 8, borderRadius: 4 }}
              color="secondary"
            />
            {data.data_coverage_pct !== undefined && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Data coverage {data.data_coverage_pct.toFixed(0)}%
                {data.data_coverage_pct < 100 ? " — confidence is reduced for missing inputs" : ""}
              </Typography>
            )}
          </Grid>
        </Grid>

        {data.edge_check?.negative_edge && (
          <Alert severity={data.edge_check.blocked ? "error" : "warning"} icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
            {data.edge_check.blocked ? "Signal downgraded to HOLD. " : ""}
            {data.edge_check.message}
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <MetricTile label="Entry Zone" value={entryZone} />
          <MetricTile label="Stop Loss" value={fmtPrice(data.stop_loss)} />
          <MetricTile label="Target 1" value={fmtPrice(data.target1)} />
          <MetricTile label="Target 2" value={fmtPrice(data.target2)} />
          <MetricTile label="Risk / Reward" value={fmtRatio(data.risk_reward_ratio)} />
          <MetricTile label="Expected Holding Period" value={fmtDays(data.expected_holding_period_days)} />
        </Grid>

        {hasEntry && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            Stop = last-10-bar swing level ± 1.5 × ATR(14) beyond it; Target 1 / Target 2 = 1.5× / 2.5× the stop distance
            from entry.
          </Typography>
        )}

        <Divider sx={{ mt: 2, mb: 1.5 }} />
        <Typography variant="caption" color="text.secondary" display="block">
          {FNO_ANALYSIS_DISCLAIMER}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default FnoHeroCard;
